import React, { useCallback, ReactNode } from 'react';
import { Helmet } from 'react-helmet';
import { useSelector } from 'react-redux';
import { Box, LinearProgress, Typography, Button, SvgIcon, styled } from '@mui/material';
import { Save as SaveIcon } from '@mui/icons-material';
import * as MuiIcons from '@mui/icons-material';

import { selectUser } from 'entities/user/selectors';

import { useImpactAiPresetManagement } from './useImpactAiPresetManagement';
import { ImpactAiPreset } from './types';
import { useUnsavedChanges } from './hooks/useUnsavedChanges';
import PresetsList from './components/PresetsList';
import PresetEditor from './components/PresetEditor';
import UnsavedChangesDialog from './components/UnsavedChangesDialog';
import SavingModal from './components/SavingModal';

// Styled components
const PreviewIcon = styled(SvgIcon)(({ theme }) => ({
  fontSize: 24,
  color: theme.palette.text.primary,
}));

const AdminGuard = ({ children }: { children: ReactNode }) => {
  const user = useSelector(selectUser);

  if (!user?.isAdmin) {
    return null;
  }

  return <>{children}</>;
};

const BackOfficeImpactAiPresetsContent = () => {
  const user = useSelector(selectUser);

  const {
    // Data
    presets,
    selectedPreset,

    // State
    isLoading,
    isAnyOperationLoading,
    isEditing,
    hoveredPresetId,
    setHoveredPresetId,
    hasChanges,

    // Individual loading states for modal
    creating,
    updating,
    deleting,

    // Form state
    formData,

    // Handlers
    handleSelectPreset: originalHandleSelectPreset,
    handleCreateNew: originalHandleCreateNew,
    handleDuplicate,
    handleDelete,
    handleSave: originalHandleSave,
    handleStartEdit,
    handleCancelEdit: originalHandleCancelEdit,
    handleFormDataChange,
    handlePresetChange,
  } = useImpactAiPresetManagement();

  // Track unsaved changes
  const { confirmNavigation, showConfirmDialog, handleConfirm, handleCancel } = useUnsavedChanges({
    hasChanges,
  });

  // Enhanced handlers with unsaved changes protection
  const handleSelectPreset = useCallback(
    (preset: ImpactAiPreset) => {
      confirmNavigation(() => {
        originalHandleSelectPreset(preset);
      });
    },
    [confirmNavigation, originalHandleSelectPreset]
  );

  const handleCreateNew = useCallback(() => {
    confirmNavigation(() => {
      originalHandleCreateNew();
    });
  }, [confirmNavigation, originalHandleCreateNew]);

  const handleSave = useCallback(async () => {
    await originalHandleSave();
  }, [originalHandleSave]);

  const handleCancelEdit = useCallback(() => {
    confirmNavigation(() => {
      originalHandleCancelEdit();
    });
  }, [confirmNavigation, originalHandleCancelEdit]);

  const handleDuplicateWrapper = (preset: ImpactAiPreset, event: React.MouseEvent) => {
    event.stopPropagation();
    confirmNavigation(async () => {
      try {
        await handleDuplicate(preset.id);
      } catch (err) {
        // Error handling is done in the hook
      }
    });
  };

  const handleDeleteWrapper = async (preset: ImpactAiPreset, event: React.MouseEvent) => {
    event.stopPropagation();
    // This is just BackOffice
    // eslint-disable-next-line no-alert
    if (window.confirm(`Are you sure you want to delete "${preset.title}"?`)) {
      try {
        await handleDelete(preset.id);
      } catch (err) {
        // Error handling is done in the hook
      }
    }
  };

  if (!user?.organization) {
    return <LinearProgress />;
  }

  if (isLoading && presets.length === 0) {
    return <LinearProgress />;
  }

  return (
    <>
      <Helmet title="ImpactAI Presets Management" />

      <Box maxWidth="100%" height="100vh" display="flex" flexDirection="column">
        <Box p={3} borderBottom="1px solid #e0e0e0">
          <Typography variant="h4" fontWeight={500} mb={1}>
            ImpactAI Presets Management
          </Typography>
          <Typography color="text.secondary">
            Manage and configure ImpactAI presets for your organization
          </Typography>
        </Box>

        <Box flex={1} display="flex" overflow="hidden">
          {/* Left Panel - Preset List */}
          <PresetsList
            presets={presets}
            selectedPreset={selectedPreset}
            hoveredPresetId={hoveredPresetId}
            isAnyOperationLoading={isAnyOperationLoading}
            onSelectPreset={handleSelectPreset}
            onCreateNew={handleCreateNew}
            onDuplicate={handleDuplicateWrapper}
            onDelete={handleDeleteWrapper}
            onHoverPreset={setHoveredPresetId}
          />

          {/* Right Panel - Edit Area */}
          <Box flex={1} display="flex" flexDirection="column">
            {selectedPreset ? (
              <>
                <Box
                  p={2}
                  borderBottom="1px solid #e0e0e0"
                  display="flex"
                  alignItems="center"
                  gap={2}
                >
                  {(() => {
                    const IconComponent = selectedPreset?.iconName
                      ? (MuiIcons as any)[selectedPreset.iconName]
                      : null;
                    return IconComponent ? (
                      <PreviewIcon sx={{ color: selectedPreset.color || '#6366f1' }}>
                        <IconComponent />
                      </PreviewIcon>
                    ) : null;
                  })()}
                  <Box flex={1}>
                    <Typography variant="h6">
                      {selectedPreset.title}
                      {hasChanges && ' *'}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      ID: {selectedPreset.id}
                    </Typography>
                  </Box>
                  {isEditing ? (
                    <Box display="flex" gap={1}>
                      <Button
                        variant="outlined"
                        size="small"
                        onClick={handleCancelEdit}
                        disabled={isAnyOperationLoading}
                      >
                        Cancel
                      </Button>
                      <Button
                        variant="contained"
                        size="small"
                        startIcon={<SaveIcon />}
                        onClick={handleSave}
                        disabled={isAnyOperationLoading || !hasChanges}
                      >
                        {isAnyOperationLoading ? 'Saving...' : 'Save'}
                      </Button>
                    </Box>
                  ) : (
                    <Button
                      variant="outlined"
                      size="small"
                      onClick={handleStartEdit}
                      disabled={isAnyOperationLoading}
                    >
                      Edit
                    </Button>
                  )}
                </Box>

                <PresetEditor
                  preset={selectedPreset}
                  isEditing={isEditing}
                  formData={formData}
                  onFormDataChange={handleFormDataChange}
                  onPresetChange={handlePresetChange}
                  availablePresets={presets}
                />
              </>
            ) : (
              <Box
                flex={1}
                display="flex"
                alignItems="center"
                justifyContent="center"
                color="text.secondary"
              >
                <Typography variant="h6">Select a preset to view and edit</Typography>
              </Box>
            )}
          </Box>
        </Box>
      </Box>

      <UnsavedChangesDialog
        open={showConfirmDialog}
        onConfirm={handleConfirm}
        onCancel={handleCancel}
      />

      <SavingModal
        open={isAnyOperationLoading}
        creating={creating}
        updating={updating}
        deleting={deleting}
      />
    </>
  );
};

const BackOfficeImpactAiPresets = () => (
  <AdminGuard>
    <BackOfficeImpactAiPresetsContent />
  </AdminGuard>
);

export default BackOfficeImpactAiPresets;
